<script setup lang="ts">
import { onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IonContent, IonButton, IonIcon, IonItem, IonList, IonPopover, IonSpinner } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import { addOutline, chevronBackOutline, chevronDownOutline, chevronForwardOutline } from 'ionicons/icons';

import TopBar from '@/shared/components/TopBar.vue';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import useRfidGuest from '@/modules/rfid/composables/useRfidGuest';
import useTenant from '@/shared/composables/useTenant';
import { captureMessage } from '@/sentry';
import { Wallet } from '@/shared/interfaces/balance';

const { t } = useI18n();
const assetStore = useAssetsStore();
const router = useRouter();
const route = useRoute();

const encryptedRfidTag = route.params.id?.toString?.() || '';
const { searchRfidTag } = useRfidGuest();
const { isSuccessRfidTag, rfidTag, rfidTagMutate } = searchRfidTag();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();

const selectedWallet = ref<Wallet | undefined>();
const wallets = ref();

onMounted(async () => {
	if (encryptedRfidTag) {
		rfidTagMutate({ encrypted_rfid_tag: encryptedRfidTag });
	}
});

watchEffect(() => {
	wallets.value = rfidTag.value?.wallets;

	if (wallets.value && wallets.value.length && wallets.value.length > 0) {
		if (!selectedWallet.value) {
			selectedWallet.value = wallets.value.find((wallet: Wallet) => {
				return wallet.currency.default;
			});

			if (!selectedWallet.value && wallets.value.length > 0) {
				selectedWallet.value = wallets.value[0];
			}
		}
	}
});

const selectWallet = (wallet: Wallet | undefined) => {
	if (!wallet) {
		captureMessage(`Attempted to select a wallet that does not exist`);
	}
	selectedWallet.value = wallet;
};

const getWalletAtIndex = (index: number) => {
	if (index >= wallets.value!.length) {
		index = 0;
	} else if (index < 0) {
		index = wallets.value!.length - 1;
	}
	return wallets.value?.[index];
};

const currentWalletIndex = () => {
	return wallets.value!.findIndex((wallet: Wallet) => wallet.id === selectedWallet.value!.id);
};

const getPreviousWallet = () => {
	return getWalletAtIndex(currentWalletIndex() - 1);
};

const getNextWallet = () => {
	return getWalletAtIndex(currentWalletIndex() + 1);
};

const selectPreviousWallet = () => {
	selectWallet(getPreviousWallet());
};

const selectNextWallet = () => {
	selectWallet(getNextWallet());
};

const goBack = async () => {
	await router.push({ path: '/dashboard' });
};
</script>

<template>
	<ion-content>
		<TopBar class="px-[34px]">
			<template #left>
				<ion-button class="w-[35px] h-[35px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full" shape="round" @click="goBack()">
					<ion-icon :icon="chevronBackOutline" class="ion-no-padding ion-no-margin text-black mr-[2px]"></ion-icon>
				</ion-button>
			</template>
			<template #center>
				<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2">
					{{ t('guest_top_up.title') }}
				</div>
			</template>
		</TopBar>

		<div>
			<div class="px-[34px] icon flex justify-center mb-4">
				<img :src="assetStore.logoUrl" class="max-w-[70%]" />
			</div>

			<!-- Wallet Box -->
			<div class="flex">
				<div class="flex w-[34px] min-w-[34px] items-center justify-center cursor-pointer">
					<ion-icon v-if="wallets && wallets?.length > 1" :icon="chevronBackOutline" class="text-white active:bg-white/30 active:rounded-full active:p-1" @click="selectPreviousWallet()"></ion-icon>
				</div>
				<div class="flex w-full flex-col justify-between px-3 bg-white rounded-xl mb-2" :class="rfidTag ? 'py-3' : 'pt-3'">
					<div class="flex items-center justify-between mb-4">
						<img :src="require('@/statics/svg/rfid/bracelet-with-bg.svg')" height="34" width="34" v-if="rfidTag" />
						<div v-if="rfidTag">
							<div class="flex justify-between mb-2">
								<div></div>
								<div v-if="selectedWallet">
									<div id="popover-button" class="flex items-center justify-center rounded-lg font-sourcesanspro-bold text-anykrowd-dark text-md cursor-pointer">
										<div class="mr-2">{{ selectedWallet.currency.prefix }}</div>
										<div class="mr-2">{{ selectedWallet?.currency.name }}</div>
										<ion-icon v-if="rfidTag?.wallets && rfidTag?.wallets?.length > 1" :icon="chevronDownOutline" class="text-anykrowd-dark text-md"></ion-icon>
									</div>
									<ion-popover trigger="popover-button" :dismiss-on-select="true">
										<ion-content>
											<ion-list>
												<ion-item v-for="wallet in rfidTag?.wallets" class="flex items-center justify-center ion-no-padding ion-no-margin" :key="wallet.id" :button="true" @click="selectWallet(wallet)" :detail="false">
													<div class="flex flex-col px-3 py-2 font-sourcesanspro-regular">
														<div class="mb-1">
															<span>
																{{ wallet.currency.prefix }}
																{{ wallet.current_balance }}
																{{ wallet.currency.name }}
															</span>
														</div>
													</div>
												</ion-item>
											</ion-list>
										</ion-content>
									</ion-popover>
								</div>
							</div>
						</div>
						<div v-if="!rfidTag && isSuccessRfidTag" class="w-full flex justify-center font-sourcesanspro-bold text-anykrowd-dark text-md pt-4">
							{{ t('guest_top_up.not_found') }}
						</div>
					</div>
					<div class="flex items-center mb-4">
						<div class="flex flex-col">
							<span v-if="rfidTag" class="font-sourcesanspro-black text-l2 font-color-dark leading-none">
								{{ rfidTag.user ? `${rfidTag.user.firstname} ${rfidTag.user.lastname}` : `${t('rfid.guest.wallet')} #${selectedWallet?.id}` }}
							</span>
							<span v-if="rfidTag" class="font-sourcesanspro-regular text-base font-color-dark leading-none">
								{{ rfidTag.uuid }}
							</span>
						</div>
					</div>

					<div class="flex items-center justify-between">
						<div class="flex">
							<ion-spinner v-if="!isSuccessRfidTag && !tenantConfig" name="dots" class="spinner-black"></ion-spinner>
							<div v-if="selectedWallet && tenantConfig" class="flex items-baseline">
								<div class="mr-1">
									<span class="font-sourcesanspro-black text-lg3 font-color-dark"> {{ selectedWallet?.currency.prefix }}{{ selectedWallet?.current_balance }} </span>
								</div>
								<span class="font-sourcesanspro-regular text-md font-color-dark">{{ selectedWallet.currency.suffix }}</span>
							</div>
						</div>
						<div v-if="selectedWallet && selectedWallet!.currency.topupable == 1" class="flex items-center">
							<ion-button class="w-[117px] h-[40px] black font-sourcesanspro-semibold text-sm2 ion-no-margin" shape="round" :routerLink="`/top-up/${selectedWallet?.id}?id=${encryptedRfidTag}`">
								<div class="mr-2"><ion-icon :icon="addOutline"></ion-icon></div>
								{{ t('rfid.guest.add') }}
							</ion-button>
						</div>
					</div>

					<div v-if="selectedWallet && selectedWallet.currency?.description" class="flex flex-col font-sourcesanspro-semibold text-anykrowd-dark/50 text-sm mb-6 mt-4">
						{{ selectedWallet.currency?.description }}
					</div>
				</div>
				<div class="flex w-[34px] min-w-[34px] items-center justify-center cursor-pointer">
					<ion-icon v-if="wallets && wallets?.length > 1" :icon="chevronForwardOutline" class="text-white active:bg-white/30 active:rounded-full active:p-1" @click="selectNextWallet()"></ion-icon>
				</div>
			</div>
		</div>
	</ion-content>
</template>

<style scoped></style>
